import { useEffect, useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import {
  getCommissionRevokePreview,
  PROJECT_WORKFLOW_STATUS_LABELS,
  type CommissionRevokePreview,
} from "@/lib/projects";
import { PROJECT_OVERVIEW_NESTED_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

export type CommissionRevokeConfirm = {
  begruendung: string;
  notiz: string;
};

export function ProjectCommissionRevokeDialog({
  open,
  projectId,
  targetStatus,
  loading,
  onClose,
  onConfirm,
}: {
  open: boolean;
  projectId: number;
  targetStatus: "angeboten" | "offen" | null;
  loading: boolean;
  onClose: () => void;
  onConfirm: (payload: CommissionRevokeConfirm) => void;
}) {
  const [begruendung, setBegruendung] = useState("");
  const [notiz, setNotiz] = useState("");
  const [preview, setPreview] = useState<CommissionRevokePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    if (!open) return;
    setBegruendung("");
    setNotiz("");
  }, [open, projectId, targetStatus]);

  useEffect(() => {
    if (!open || !targetStatus) return;
    let cancelled = false;
    setPreview(null);
    setPreviewError("");
    setPreviewLoading(true);
    void getCommissionRevokePreview(projectId, targetStatus)
      .then((row) => {
        if (!cancelled) setPreview(row);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPreviewError(
            err instanceof Error ? err.message : "Die Beauftragung konnte nicht gelesen werden.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId, targetStatus]);

  const zielLabel =
    preview?.ziel_label ||
    (targetStatus ? PROJECT_WORKFLOW_STATUS_LABELS[targetStatus] : "");
  const aktuellLabel = preview?.aktueller_label || "Beauftragt";
  const trimmedReason = begruendung.trim();
  const trimmedNote = notiz.trim();
  const blocked = Boolean(preview && !preview.can_apply);
  const canSubmit = Boolean(
    targetStatus && trimmedReason && preview?.can_apply && !previewLoading && !previewError,
  );

  return (
    <Dialog
      open={open}
      onClose={loading ? () => {} : onClose}
      dirty={trimmedReason.length > 0 || trimmedNote.length > 0}
      size="md"
      zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
      plainBackdrop
      title="Beauftragung zurücknehmen"
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={loading} />
          <Button
            type="button"
            disabled={!canSubmit}
            loading={loading}
            onClick={() => onConfirm({ begruendung: trimmedReason, notiz: trimmedNote })}
          >
            Beauftragung zurücknehmen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm leading-relaxed text-label-secondary">
          Status von „{aktuellLabel}“ auf „{zielLabel}“ setzt die Beauftragung zurück.
          Begründung und Notiz werden mit Person und Zeitpunkt in die Projektnotizen geschrieben.
        </p>
        {previewLoading ? (
          <div className="flex items-center gap-2 text-sm text-label-secondary">
            <Spinner />
            Beauftragung wird geprüft …
          </div>
        ) : null}
        {previewError ? <Alert variant="error">{previewError}</Alert> : null}
        {preview?.can_apply && preview.actions.length > 0 ? (
          <div className="space-y-1">
            <p className="text-xs font-medium text-label-tertiary">Das wird zurückgenommen</p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-label-secondary">
              {preview.actions.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {blocked ? (
          <Alert variant="warning" title="Status bleibt so nicht stehen">
            <ul className="list-disc space-y-1 pl-5">
              {preview?.blockers.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        {preview?.hero_unveraendert && preview.can_apply ? (
          <Alert variant="info">Der Projekt-Schritt in Hero bleibt unverändert.</Alert>
        ) : null}
        <div className="space-y-1">
          <label className="eh-label" htmlFor="po-commission-revoke-grund">
            Begründung<span className="ml-1 text-status-red">*</span>
          </label>
          <textarea
            id="po-commission-revoke-grund"
            className="eh-input min-h-[5rem]"
            rows={4}
            placeholder="Warum ist das Projekt nicht beauftragt?"
            value={begruendung}
            disabled={loading || blocked}
            onChange={(e) => setBegruendung(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="eh-label" htmlFor="po-commission-revoke-notiz">
            Notiz
          </label>
          <textarea
            id="po-commission-revoke-notiz"
            className="eh-input min-h-[4rem]"
            rows={3}
            placeholder="Optional, wird zusätzlich in die Projektnotiz geschrieben"
            value={notiz}
            disabled={loading || blocked}
            onChange={(e) => setNotiz(e.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}
